import React from "react";
import { Breadcrumbs, TabsWithUnderline } from "@truevault/aquilo";
import type { Tab } from "@truevault/aquilo/src/components/tabs/TabsWithUnderline";
import { useHistory } from "react-router";

export type DataFlowsHeaderTab = Omit<Tab, "onClick"> & { route: string; tabKey?: string };

/**
 * One content width for every card on the Data Flows pages — the help card, the collection
 * group panels and the tab underline all measure the same, so the page reads as a single
 * column instead of three slightly different ones.
 */
export const DATA_FLOWS_CONTENT_MAX_WIDTH = 1143;

/**
 * Shared breadcrumb + sticky tab bar for the Data Flows dashboard and Sources pages.
 *
 * The breadcrumb block pulls the page wrapper's 64px top padding back to a 24px gap, matching
 * the vendor inventory header (pol-7592). Header rhythm is 24px between each item: the
 * Breadcrumbs component has 24px between trail and title built in, and the 6px below plus the
 * tab items' own 16px top padding put the title-to-tab-label gap at 24px as well.
 *
 * The tabs pin while the content scrolls — same technique as the vendor inventory toolbar:
 * white background extended across the left padding gutter (the negative-margin/extra-width
 * trick below) so scrolling content doesn't peek out on the left. zIndex 9 stays below the
 * aquilo Slideout's z-10 so an open panel renders on top of this bar.
 */
export function DataFlowsStickyHeader({
  breadcrumb,
  tabs,
}: {
  breadcrumb?: { title: string; source: string; sourceRoute: string };
  tabs: DataFlowsHeaderTab[];
}) {
  const history = useHistory();

  return (
    <>
      {breadcrumb && (
        <div style={{ marginTop: -40, marginBottom: 6 }}>
          <Breadcrumbs
            hideHomeIcon={true}
            title={breadcrumb.title}
            source={breadcrumb.source}
            sourceRoute={breadcrumb.sourceRoute}
          />
        </div>
      )}
      {tabs.length > 1 && (
        <div
          style={{
            position: "sticky",
            top: 0,
            zIndex: 9,
            width: "calc(100% + 64px)",
            marginLeft: -64,
            paddingLeft: 64,
            backgroundColor: "white",
          }}
        >
          {/* The underline is capped at the content width so it lines up with the cards below
              instead of running past them, while the white background still bleeds across the
              gutter to hide content scrolling underneath. The tab items carry 16px of their own
              top padding, which is the visible gap under the page title. */}
          <div style={{ maxWidth: DATA_FLOWS_CONTENT_MAX_WIDTH }}>
            <TabsWithUnderline
              tabs={tabs.map(({ route, ...tab }) => ({
                ...tab,
                onClick: () => {
                  history.push(route);
                },
              }))}
            />
          </div>
        </div>
      )}
    </>
  );
}
